// The structured document an answer carries (message.document): one card
// under the answer (design review ui.region_gate: card, first blocks with
// Tampilkan semua). The title, a compact preview of the blocks (tables scroll
// sideways inside the card, never the page), the files already made from it
// as download chips, and the actions: Unduh DOCX, Unduh XLSX (only with a
// table), Cetak / PDF (a print view of the same blocks). Unduh builds the
// file on request from the stored document, so it works without Google
// Drive; a connected Drive keeps a copy on the API side.
import { Fragment, useState, type ReactNode } from "react";
import * as API from "../api";
import type { AttachmentRef, DocumentBlock, GeneratedFileFormat, StructuredDocument } from "../api";
import { saveFile } from "../pbj/docx";
import { Glyph } from "../ui/icons";
import { AttachmentLink } from "./AttachmentChips";
import { PrintDocument } from "./PrintDocument";

/** Blocks shown before Tampilkan semua. */
export const PREVIEW_BLOCKS = 6;

export function hasTable(doc: StructuredDocument): boolean {
  return doc.blocks.some((b) => b.type === "table");
}

/** Text with its line breaks kept ("Nama Paket: ..." and "Instansi: ..." on their own lines). */
export function withBreaks(text: string): ReactNode {
  const lines = text.split(/\r?\n/);
  if (lines.length === 1) return text;
  return lines.map((line, i) => (
    <Fragment key={i}>
      {i > 0 ? <br /> : null}
      {line}
    </Fragment>
  ));
}

/** The blocks as plain markup; `print` uses real headings under the title. */
export function DocumentBlocks({ blocks, print = false }: { blocks: DocumentBlock[]; print?: boolean }) {
  return (
    <>
      {blocks.map((b, i) => {
        switch (b.type) {
          case "heading": {
            if (print) {
              const H = (["h2", "h3", "h4"] as const)[Math.min(3, Math.max(1, b.level)) - 1]!;
              return (
                <H key={i} className="doc-block-h" data-level={b.level}>
                  {b.text}
                </H>
              );
            }
            return (
              <p key={i} className="doc-block-h" data-level={b.level}>
                {withBreaks(b.text)}
              </p>
            );
          }
          case "paragraph":
            return (
              <p key={i} className="doc-block-p">
                {withBreaks(b.text)}
              </p>
            );
          case "list": {
            const L = b.ordered ? "ol" : "ul";
            return (
              <L key={i} className="doc-block-list">
                {b.items.map((it, j) => (
                  <li key={j}>{withBreaks(it)}</li>
                ))}
              </L>
            );
          }
          case "table":
            return (
              <div key={i} className="doc-block-table" role={print ? undefined : "region"} aria-label={print ? undefined : "Tabel"} tabIndex={print ? undefined : 0}>
                <table>
                  <thead>
                    <tr>
                      {b.headers.map((h, j) => (
                        <th key={j} scope="col">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.map((row, j) => (
                      <tr key={j}>
                        {row.map((cell, k) => (
                          <td key={k}>{withBreaks(cell)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          default:
            return null;
        }
      })}
    </>
  );
}

/** A file action's failure in Indonesian. */
export function fileErrorMessage(err: unknown, format: GeneratedFileFormat): string {
  if (err instanceof API.ApiError) {
    if (err.status === 422) return format === "xlsx" ? "Dokumen ini tidak memiliki tabel untuk dijadikan XLSX." : "Dokumen ini belum dapat dijadikan file.";
    if (err.status === 503) return "Penyimpanan file belum tersedia. Hubungi admin.";
    if (err.status === 429) return err.retryAfter ? `Terlalu banyak permintaan. Coba lagi dalam ${err.retryAfter} detik.` : "Terlalu banyak permintaan. Tunggu sebentar, lalu coba lagi.";
    if (err.status === 404) return "Pesan ini tidak ditemukan. Muat ulang percakapan.";
  }
  return API.errorMessage(err);
}

export interface DocumentCardProps {
  document: StructuredDocument;
  /** The message's files; the generated ones show as download chips. */
  attachments: AttachmentRef[];
  /** DOCX and XLSX need the stored message; null hides them (print still works). */
  conversationId: string | null;
  messageId: string;
}

export function DocumentCard({ document: doc, attachments, conversationId, messageId }: DocumentCardProps) {
  // Copies already kept in the user's Drive (made before, or by a connected Drive).
  const made = attachments.filter((a) => a.kind === "generated");
  const [busy, setBusy] = useState<GeneratedFileFormat | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const [printing, setPrinting] = useState(false);
  const table = hasTable(doc);
  const shown = all ? doc.blocks : doc.blocks.slice(0, PREVIEW_BLOCKS);
  const rest = doc.blocks.length - shown.length;
  const title = doc.title.trim() || "Dokumen";

  const make = async (format: GeneratedFileFormat) => {
    if (!conversationId || busy) return;
    setBusy(format);
    setError(null);
    try {
      // Built on request from the stored document: no Drive needed.
      saveFile(await API.exportMessageFile(conversationId, messageId, format));
    } catch (err) {
      setError(fileErrorMessage(err, format));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="doc-card-chat" aria-label={`Dokumen: ${title}`}>
      <div className="doc-card-head">
        <span className="doc-card-icon" aria-hidden="true">
          <Glyph name="documentText" size={20} />
        </span>
        <p className="doc-card-title">{title}</p>
      </div>
      <div className="doc-card-body">
        <DocumentBlocks blocks={shown} />
      </div>
      {rest > 0 || all ? (
        <button type="button" className="btn btn-ghost doc-card-more" aria-expanded={all} onClick={() => setAll((v) => !v)}>
          {all ? "Tampilkan ringkas" : `Tampilkan semua (${rest} bagian lagi)`}
        </button>
      ) : null}
      {made.length > 0 ? (
        <ul className="att-chips doc-card-files" aria-label="File dokumen">
          {made.map((a) => (
            <li key={a.id}>
              <AttachmentLink a={a} />
            </li>
          ))}
        </ul>
      ) : null}
      <div className="doc-card-actions">
        {conversationId ? (
          <button type="button" className="btn btn-secondary" aria-busy={busy === "docx" ? true : undefined} disabled={busy !== null && busy !== "docx"} onClick={() => void make("docx")}>
            <Glyph name="documentDownload" />
            <span>Unduh DOCX</span>
          </button>
        ) : null}
        {conversationId && table ? (
          <button type="button" className="btn btn-secondary" aria-busy={busy === "xlsx" ? true : undefined} disabled={busy !== null && busy !== "xlsx"} onClick={() => void make("xlsx")}>
            <Glyph name="documentDownload" />
            <span>Unduh XLSX</span>
          </button>
        ) : null}
        <button type="button" className="btn btn-secondary" onClick={() => setPrinting(true)}>
          <Glyph name="printer" />
          <span>Cetak / PDF</span>
        </button>
      </div>
      {busy ? (
        <p className="doc-card-status" role="status">
          Menyiapkan file {busy.toUpperCase()}
        </p>
      ) : null}
      {error ? (
        <p className="composer-hint doc-card-error" data-state="error" role="alert">
          <Glyph name="danger" size={16} />
          <span>{error}</span>
        </p>
      ) : null}
      {printing ? <PrintDocument document={doc} onDone={() => setPrinting(false)} /> : null}
    </section>
  );
}
